import { useState, useEffect, useCallback, useRef } from 'react';
import { Newspaper, RefreshCw, ExternalLink, AlertTriangle } from 'lucide-react';

const API_BASE = 'https://tickertapapi.avetter90.workers.dev';

const CATEGORIES = [
  { label: 'ALL',      emoji: '📋' },
  { label: 'MARKETS',  emoji: '📊' },
  { label: 'FED',      emoji: '🟡' },
  { label: 'CRYPTO',   emoji: '⚡' },
  { label: 'METALS',   emoji: '🥇' },
  { label: 'OIL',      emoji: '🛢️' },
  { label: 'AI',       emoji: '🤖' },
  { label: 'CHINA',    emoji: '🌏' },
  { label: 'POLITICS', emoji: '🏛️' },
  { label: 'WAR',      emoji: '🔴' },
  { label: 'MIDEAST',  emoji: '🔴' },
  { label: 'NEWS',     emoji: '📰' },
];

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ArticleCard({ article }) {
  const { headline, url, source, category, emoji, published_at } = article;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
    >
      <div
        className="px-4 py-3 rounded-xl border transition-colors duration-150 group-hover:border-[#2a2a2a]"
        style={{ background: '#0f0f0f', borderColor: '#1a1a1a' }}
      >
        <div className="flex items-start gap-3">
          {/* Category badge */}
          <span className="text-base mt-0.5 flex-shrink-0">{emoji}</span>

          <div className="flex-1 min-w-0">
            {/* Headline */}
            <p className="text-sm text-gray-200 leading-snug group-hover:text-white transition-colors line-clamp-2">
              {headline}
            </p>

            {/* Meta row */}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span
                className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded"
                style={{ background: '#1a1a1a', color: '#6b7280' }}
              >
                {category}
              </span>
              {source && (
                <span className="text-xs text-gray-600 truncate max-w-[140px]">{source}</span>
              )}
              <span className="text-xs text-gray-700 ml-auto flex-shrink-0">{timeAgo(published_at)}</span>
            </div>
          </div>

          {/* Link icon */}
          <ExternalLink className="w-3.5 h-3.5 text-gray-700 group-hover:text-gray-400 flex-shrink-0 mt-0.5 transition-colors" />
        </div>
      </div>
    </a>
  );
}

function CategoryTab({ cat, active, onClick }) {
  return (
    <button
      onClick={() => onClick(cat.label)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors"
      style={{
        background: active ? '#22d3ee18' : '#0f0f0f',
        color: active ? '#22d3ee' : '#6b7280',
        border: `1px solid ${active ? '#22d3ee33' : '#1a1a1a'}`
      }}
    >
      <span>{cat.emoji}</span>
      <span>{cat.label}</span>
    </button>
  );
}

// ============================================================
// MAIN
// ============================================================
export default function NewsFeed() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const LIMIT = 50;

  const fetchNews = useCallback(async (cat, off, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ category: cat, limit: LIMIT, offset: off });
      const res = await fetch(`${API_BASE}/api/news?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const incoming = json.articles || [];

      setArticles(prev => append ? [...prev, ...incoming] : incoming);
      setHasMore(incoming.length === LIMIT);
      setLastFetched(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load + category change
  useEffect(() => {
    setOffset(0);
    setArticles([]);
    fetchNews(category, 0, false);
  }, [category, fetchNews]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setOffset(0);
      fetchNews(category, 0, false);
    }, 120000);
    return () => clearInterval(interval);
  }, [category, fetchNews]);

  const handleLoadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    fetchNews(category, newOffset, true);
  };

  const handleCategoryChange = (cat) => {
    if (cat === category) return;
    setCategory(cat);
  };

  return (
    <div className="p-4 space-y-4" style={{ background: '#0a0a0a', minHeight: '100vh' }}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Newspaper className="w-4 h-4" style={{ color: '#22d3ee' }} />
          <h1 className="text-base font-semibold text-gray-100">News Feed</h1>
        </div>
        <div className="flex items-center gap-3">
          {lastFetched && (
            <span className="text-xs text-gray-700">
              {lastFetched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => { setOffset(0); fetchNews(category, 0, false); }}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Category filter bar */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {CATEGORIES.map(cat => (
          <CategoryTab
            key={cat.label}
            cat={cat}
            active={category === cat.label}
            onClick={handleCategoryChange}
          />
        ))}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            Loading {category === 'ALL' ? 'news' : category} articles...
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-lg border"
          style={{ background: '#0f0f0f', borderColor: '#1a1a1a' }}
        >
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-xs text-red-400">{error}</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && articles.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Newspaper className="w-8 h-8 text-gray-700 mb-3" />
          <p className="text-sm text-gray-500">No articles yet</p>
          <p className="text-xs text-gray-700 mt-1">
            {category === 'ALL'
              ? 'News will appear here as the bot posts'
              : `No ${category} articles in the feed yet`}
          </p>
        </div>
      )}

      {/* Article list */}
      {!loading && articles.length > 0 && (
        <div className="space-y-2">
          {articles.map(article => (
            <ArticleCard key={article.id} article={article} />
          ))}

          {/* Load more */}
          {hasMore && (
            <div className="pt-2 flex justify-center">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs text-gray-500 hover:text-gray-300 border border-[#1a1a1a] hover:border-[#2a2a2a] transition-colors"
                style={{ background: '#0f0f0f' }}
              >
                {loadingMore ? (
                  <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading...</>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}

          {/* End of feed */}
          {!hasMore && articles.length > 0 && (
            <p className="text-center text-xs text-gray-700 pt-2">End of feed</p>
          )}
        </div>
      )}
    </div>
  );
}
